import { QueryList } from '@angular/core';
import { ModalActionBtnsComponent } from '../components/button/modal-action-btns/modal-action-btns.component';

/**
 * SubmitGuard - Prevents double form submissions
 * 
 * Usage with submit buttons:
 * ```typescript
 * @ViewChildren(ModalActionBtnsComponent) submitButtons!: QueryList<ModalActionBtnsComponent>;
 * private submitGuard = new SubmitGuard(() => this.submitButtons);
 * 
 * onSubmit = () => {
 *   return this.submitGuard.guard(() => this.performSubmit());
 * };
 * ```
 * 
 * Usage without submit buttons (simple flag):
 * ```typescript
 * private submitGuard = new SubmitGuard();
 * 
 * onSubmit = () => {
 *   return this.submitGuard.guard(() => this.performSubmit());
 * };
 * ```
 */
export class SubmitGuard {
  private isSubmitting = false;

  constructor(private readonly getSubmitButtons?: () => QueryList<ModalActionBtnsComponent> | undefined) {}

  /**
   * Wraps an async submit function with double-click prevention.
   * Sets button states to submitting, executes the function, and resets on error.
   * 
   * @param submitFn - The async function to execute
   * @returns The result of submitFn, or undefined if already submitting
   */
  async guard<T>(submitFn: () => Promise<T>): Promise<T | undefined> {
    if (this.isSubmitting) {
      return undefined;
    }

    this.isSubmitting = true;
    this.setButtonsSubmitting(true);

    try {
      return await submitFn();
    } catch (err) {
      this.reset();
      throw err;
    }
  }

  /**
   * Synchronous guard check - use when you need more control over the flow
   * @returns true if submission can proceed, false if already submitting
   */
  tryStart(): boolean {
    if (this.isSubmitting) {
      return false;
    }
    this.isSubmitting = true;
    this.setButtonsSubmitting(true);
    return true;
  }

  /**
   * Resets the submit state and button states
   */
  reset(): void {
    this.isSubmitting = false;
    this.setButtonsSubmitting(false);
  }

  /**
   * Returns whether a submission is currently in progress
   */
  get submitting(): boolean {
    return this.isSubmitting;
  }

  private setButtonsSubmitting(submitting: boolean): void {
    if (!this.getSubmitButtons) {
      return;
    }
    const buttons = this.getSubmitButtons();
    if (buttons) {
      buttons.forEach(button => {
        if (submitting) {
          button.setSubmittingState(true);
        } else {
          button.resetSubmittingState();
        }
      });
    }
  }
}
